// Export utility functions for stopwatch system
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import notify from 'devextreme/ui/notify';

export const exportToCSV = async (data, filename = 'stopwatch_results') => {
    const headers = ['Sportler', 'Zeiten'];
    const csvContent = [
        headers.join(';'),
        ...data.map(row => 
            headers.map(header => {
                const value = row[header] || '';
                // Escape commas and quotes in CSV
                return `"${value.toString().replace(/"/g, '""')}"`;
            }).join(';')
        )
    ].join('\n');

    // Add BOM for proper encoding
    const csvWithBOM = '\ufeff' + csvContent;

    if (Capacitor.isNativePlatform()) {
        try {
            // Mobile platform - use Capacitor Filesystem
            const fileName = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
            
            await Filesystem.writeFile({
                path: fileName,
                data: csvWithBOM,
                directory: Directory.Documents,
                encoding: Encoding.UTF8
            });

            // Share the file
            const fileUri = await Filesystem.getUri({
                directory: Directory.Documents,
                path: fileName
            });

            await Share.share({
                title: 'Stoppuhr Ergebnisse',
                text: 'Exportierte Zeitmessung',
                url: fileUri.uri,
                dialogTitle: 'CSV-Datei teilen'
            });

            notify('CSV-Datei erfolgreich exportiert und geteilt', 'success', 3000);
        } catch (error) {
            console.error('Fehler beim Exportieren:', error);
            notify('Fehler beim Exportieren der CSV-Datei: ' + error.message, 'error', 5000);
        }
    } else {
        // Web platform - use traditional download
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${filename}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            notify('CSV-Datei heruntergeladen', 'success', 3000);
        }
    }
};

export const exportToExcel = async (data, filename = 'stopwatch_results') => {
    // For now, we'll use CSV format with proper mobile support
    // Could be extended with proper Excel libraries in the future
    await exportToCSV(data, filename);
};

export const printResults = async (data) => {
    if (Capacitor.isNativePlatform()) {
        // Mobile platform - create a PDF-style export via Share
        try {
            const htmlContent = `
                <h1>Stoppuhr Ergebnisse</h1>
                <p>Gedruckt am: ${new Date().toLocaleString('de-DE')}</p>
                <table border="1" style="border-collapse: collapse; width: 100%;">
                    <thead>
                        <tr style="background-color: #f2f2f2;">
                            <th style="padding: 8px;">Sportler</th>
                            <th style="padding: 8px;">Zeiten</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(row => `
                            <tr>
                                <td style="padding: 8px;">${row.Sportler}</td>
                                <td style="padding: 8px;">${row.Zeiten}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
            
            const fileName = `stoppuhr_ergebnisse_${new Date().toISOString().split('T')[0]}.html`;
            
            await Filesystem.writeFile({
                path: fileName,
                data: htmlContent,
                directory: Directory.Documents,
                encoding: Encoding.UTF8
            });

            const fileUri = await Filesystem.getUri({
                directory: Directory.Documents,
                path: fileName
            });

            await Share.share({
                title: 'Stoppuhr Ergebnisse',
                text: 'Druckbare Zeitmessung',
                url: fileUri.uri,
                dialogTitle: 'Ergebnisse teilen'
            });

            notify('Ergebnisse erfolgreich zum Teilen vorbereitet', 'success', 3000);
        } catch (error) {
            console.error('Fehler beim Erstellen der Druckdatei:', error);
            notify('Fehler beim Erstellen der Druckdatei: ' + error.message, 'error', 5000);
        }
    } else {
        // Web platform - use traditional print
        const printWindow = window.open('', '_blank');
        
        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Stoppuhr Ergebnisse</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                    h1 { color: #333; text-align: center; }
                    .timestamp { text-align: center; color: #666; margin-bottom: 20px; }
                </style>
            </head>
            <body>
                <h1>Stoppuhr Ergebnisse</h1>
                <div class="timestamp">Gedruckt am: ${new Date().toLocaleString('de-DE')}</div>
                <table>
                    <thead>
                        <tr>
                            <th>Sportler</th>
                            <th>Zeiten</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(row => `
                            <tr>
                                <td>${row.Sportler}</td>
                                <td>${row.Zeiten}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;
        
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    }
};

export const formatTimeForDisplay = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ds = Math.floor((milliseconds % 1000) / 100); // Zehntelsekunden
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ds}`;
};

export const parseTimeInput = (timeString) => {
    // Parse time input in format MM:SS.D or SS.D (Zehntelsekunden)
    const timeRegex = /^(?:(\d{1,2}):)?(\d{1,2})\.(\d{1})$/;
    const match = timeString.match(timeRegex);
    
    if (!match) {
        throw new Error('Invalid time format. Use MM:SS.D or SS.D');
    }
    
    const minutes = parseInt(match[1] || '0', 10);
    const seconds = parseInt(match[2], 10);
    const deciseconds = parseInt(match[3], 10);
    
    return (minutes * 60000) + (seconds * 1000) + (deciseconds * 100);
};

export const validateGridConfiguration = (lanes, rows) => {
    const errors = [];
    
    if (lanes < 1 || lanes > 10) {
        errors.push('Anzahl der Bahnen muss zwischen 1 und 10 liegen');
    }
    
    if (rows < 1 || rows > 20) {
        errors.push('Anzahl der Reihen muss zwischen 1 und 20 liegen');
    }
    
    return {
        isValid: errors.length === 0,
        errors
    };
};

export const generateTrainingReport = (grid, recordedTimes, rows, lanes) => {
    const report = {
        summary: {
            totalAthletes: 0,
            totalTimes: 0,
            averageTime: 0,
            bestTime: null,
            worstTime: null
        },
        details: [],
        timestamp: new Date().toISOString()
    };
    
    const allTimes = [];
    
    for (let row = 0; row < rows; row++) {
        for (let lane = 0; lane < lanes; lane++) {
            const athlete = grid[row][lane];
            if (athlete) {
                report.summary.totalAthletes++;
                const key = `${row}-${lane}`;
                const times = recordedTimes[key] || [];
                
                if (times.length > 0) {
                    const athleteDetail = {
                        position: { row: row + 1, lane: lane + 1 },
                        athlete: {
                            name: `${athlete.surname} ${athlete.lastname}`,
                            club: athlete.clubname || '',
                            trainingGroup: athlete.groupname || ''
                        },
                        times: times.map(t => ({
                            value: t.time,
                            type: t.type,
                            formatted: formatTimeForDisplay(t.time)
                        })),
                        bestTime: Math.min(...times.map(t => t.time)),
                        averageTime: times.reduce((sum, t) => sum + t.time, 0) / times.length
                    };
                    
                    report.details.push(athleteDetail);
                    allTimes.push(...times.map(t => t.time));
                }
            }
        }
    }
    
    if (allTimes.length > 0) {
        report.summary.totalTimes = allTimes.length;
        report.summary.averageTime = allTimes.reduce((sum, time) => sum + time, 0) / allTimes.length;
        report.summary.bestTime = Math.min(...allTimes);
        report.summary.worstTime = Math.max(...allTimes);
    }
    
    return report;
};
